// ============================================================================
// SETTINGS API — React Query hooks for user settings endpoints
// ============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from './client'

// ============================================================================
// TYPES
// ============================================================================

export type AlertChannel = 'email' | 'instant_messaging' | 'inapp'

/** Flow AI spend over the trailing 7 days. Null when the budget is unenforced. */
export interface FlowUsage {
  used: number
  cap: number
  pct: number
}

export interface UserProfileData {
  first_name: string
  last_name: string
  email: string
  account_type: string
  is_staff?: boolean
  is_superuser?: boolean
  is_competition_mode: boolean
  flow_usage: FlowUsage | null
  biometrics: {
    date_of_birth: string | null
    biological_gender: string | null
    height_cm: number | null
    weight_kg: number | null
  }
}

export interface NotificationData {
  receive_newsletters: boolean
  enable_app_notifications: boolean
  enable_messaging_notifications: boolean
  enable_email_notifications: boolean
  enable_push_notifications: boolean
  receive_survey_reminders: AlertChannel[]
  receive_performance_summary: AlertChannel[]
  receive_alert_on_new_activity: AlertChannel[]
}

export interface PrivacyData {
  right_to_edit_modify: boolean
  right_to_data_processing: boolean
  accept_ai_health_data_processing: boolean
  advertising_consent: boolean
}

export interface SupportAccessData {
  granted: boolean
  expiry: string | null
}

export interface SubscriptionStatus {
  is_active: boolean
  has_full_access: boolean
  current_period_end: string | null
  cancel_at_period_end: boolean
  stripe_product_id: string | null
  stripe_subscription_id?: string | null
  access_state: string
  apple_product_id: string | null
  apple_original_transaction_id?: string | null
  // Canonical entitlement decision fields. Optional for compatibility with a
  // rolling deploy where an older backend can briefly answer a newer webapp.
  is_trial?: boolean
  /** When a running trial ends (the card-free coach trial, beoflow #2128). */
  trial_end?: string | null
  /** Athlete invites the trial still allows (3 in total); null when not on one. */
  trial_invites_left?: number | null
  is_paid?: boolean
  is_in_grace_period?: boolean
  grace_period_end?: string | null
  requires_payment?: boolean
  payment_required_reason?: 'missing_subscription' | 'trial_expired' | 'paid_expired' | string | null
  /** Positive backend decision; omission during a rolling deploy must fail open. */
  requires_web_paywall?: boolean
  // NOTE: `trial_days_available` is NOT here. It is served at the TOP LEVEL of
  // /subscription/status/, beside `subscription` rather than inside it, because
  // this object is null until a first purchase. Read it with
  // useTrialDaysAvailable().
  billing_provider?: 'stripe' | 'apple' | null
  recovery_action?: 'checkout' | 'billing_portal' | 'app_store' | null
  sponsorships_quota: number
  sponsorships_used: number
  sponsorships_available: number
  // Seat lane (BEO-3025 / BEO-3058; additive, may be absent on old payloads)
  seat_stripe_subscription_id?: string | null
  seat_active?: boolean
  seat_period_end?: string | null
  seat_cancel_at_period_end?: boolean
  seat_access_frozen?: boolean
}

// ============================================================================
// QUERY KEYS
// ============================================================================

const PROFILE_KEY = ['settings', 'profile']
const PRIVACY_KEY = ['settings', 'privacy']
const SUPPORT_KEY = ['settings', 'support']
export const SUBSCRIPTION_KEY = ['settings', 'subscription']

// ============================================================================
// PROFILE
// ============================================================================

export function useUserProfile() {
  return useQuery({
    queryKey: PROFILE_KEY,
    queryFn: async () => {
      const { data } = await apiClient.get<UserProfileData>('/auth_app/profile/')
      return data
    },
  })
}

/** Opens Stripe Checkout for a one-off Flow usage reset. */
export function useFlowResetCheckout() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post<{ checkout_url: string }>(
        '/subscription/flow-reset/checkout/',
      )
      return data.checkout_url
    },
  })
}

export function useUpdateProfile() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: Partial<UserProfileData>) => {
      const { data } = await apiClient.patch<UserProfileData>('/auth_app/profile/', input)
      return data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(PROFILE_KEY, data)
    },
  })
}

/** Mark athlete onboarding complete. The flag gates re-entry into the guided
 *  /onboarding wizard and is surfaced on the auth profile as
 *  `is_onboarding_completed`. iOS calls the same endpoint. */
export const completeOnboarding = () =>
  apiClient.post<{ is_onboarding_completed: boolean }>('/auth_app/profile/onboarding-complete/')

// ============================================================================
// NOTIFICATIONS
// ============================================================================

const NOTIFICATIONS_KEY = ['settings', 'notifications']

export function useNotifications() {
  return useQuery({
    queryKey: NOTIFICATIONS_KEY,
    queryFn: async () => {
      const { data } = await apiClient.get<NotificationData>('/notifications/')
      return data
    },
  })
}

export function useUpdateNotifications() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: Partial<NotificationData>) => {
      const { data } = await apiClient.patch<NotificationData>('/notifications/', input)
      return data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(NOTIFICATIONS_KEY, data)
    },
  })
}

// ============================================================================
// PRIVACY
// ============================================================================

export function usePrivacy() {
  return useQuery({
    queryKey: PRIVACY_KEY,
    queryFn: async () => {
      const { data } = await apiClient.get<PrivacyData>('/privacy/')
      return data
    },
  })
}

export function useUpdatePrivacy() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: Partial<PrivacyData>) => {
      const { data } = await apiClient.patch<PrivacyData>('/privacy/', input)
      return data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(PRIVACY_KEY, data)
    },
  })
}

// ============================================================================
// SUPPORT ACCESS
// ============================================================================

export function useSupportAccess() {
  return useQuery({
    queryKey: SUPPORT_KEY,
    queryFn: async () => {
      const { data } = await apiClient.get<SupportAccessData>('/privacy/support-access/')
      return data
    },
  })
}

export function useToggleSupportAccess() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post<SupportAccessData>('/privacy/support-access/toggle/')
      return data
    },
    onSuccess: (data) => {
      queryClient.setQueryData(SUPPORT_KEY, data)
    },
  })
}

// ============================================================================
// SUBSCRIPTION
// ============================================================================

export function useSubscription() {
  return useQuery({
    queryKey: SUBSCRIPTION_KEY,
    queryFn: async () => {
      const { data } = await apiClient.get<{ subscription: SubscriptionStatus | null }>('/subscription/status/')
      return data.subscription
    },
    // A hard-gated entitlement can change outside this tab (Apple renewal,
    // sponsorship, support grant). Refresh only that state so stale cache data
    // cannot keep someone locked out or continue offering the wrong provider.
    refetchInterval: (query) => (
      query.state.data?.requires_web_paywall === true ? 15_000 : false
    ),
    refetchOnWindowFocus: (query) => (
      query.state.data?.requires_web_paywall === true
    ),
  })
}

export function useOpenStripePortal() {
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post<{ url: string }>('/subscription/portal/')
      return data
    },
    onSuccess: (data) => {
      window.location.href = data.url
    },
  })
}

const TRIAL_DAYS_KEY = ['settings', 'subscription', 'trial-days']

/**
 * How many days of trial a checkout started NOW would apply: 7, 30 after a
 * booked setup call, and 0 for anyone who cannot be offered one.
 *
 * Read from the TOP LEVEL of /subscription/status/ and deliberately NOT through
 * `useSubscription`, which returns `data.subscription` — that object is null
 * until a first purchase, so the coach who most needs this number is exactly
 * the one for whom it would be unreadable.
 *
 * `null` means the backend did not serve the field. That is not zero: a rolling
 * deploy against an older backend must fall back to the trial the wall already
 * promises, not withdraw the offer.
 */
export function useTrialDaysAvailable() {
  return useQuery({
    queryKey: TRIAL_DAYS_KEY,
    queryFn: async () => {
      const { data } = await apiClient.get<{ trial_days_available?: number }>('/subscription/status/')
      return typeof data.trial_days_available === 'number' ? data.trial_days_available : null
    },
  })
}

/** Record that the coach booked a setup call, which is what lengthens the trial.
 *
 *  The DAYS are the server's answer, never this client's: the trial is applied
 *  to the Stripe Checkout session server-side, so a webapp that decided for
 *  itself that the trial was 30 days would print a number the invoice then
 *  contradicts. Both status queries are invalidated so the CTA re-reads
 *  `trial_days_available` rather than trusting this mutation's own return.
 *
 *  200 and idempotent for a coach; 403 `not_a_coach` for anyone else, which the
 *  caller must treat as "the trial did not change" rather than as a failure to
 *  report — the booking page is already open by then either way. */
export function useBookTrialCall() {
  const queryClient = useQueryClient()
  return useMutation({
    // `trial_days` is what a trial started NEXT would get (30 after a call).
    // While a card-free trial already runs it is 0 and `trial_end` is the
    // extended end instead (beoflow #2128).
    mutationFn: async () => {
      const { data } = await apiClient.post<{ trial_days: number; trial_end?: string | null }>('/subscription/trial-call/')
      return { trialDays: data.trial_days, trialEnd: data.trial_end ?? null }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_KEY })
      queryClient.invalidateQueries({ queryKey: TRIAL_DAYS_KEY })
    },
  })
}

/** Start the coach's card-free trial: 10 days, 30 after a booked call.
 *
 *  PROVISIONAL contract while beoflow #2128 lands: the path and the payload
 *  shape live here only, so a rename is a one-line change. Answers with the
 *  subscription status (bare or under `subscription`, read both); 409
 *  `trial_unavailable` when the account already trialled or pays. */
export const START_TRIAL_PATH = '/subscription/start-trial/'

export function useStartTrial() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const { data } = await apiClient.post<{ subscription?: SubscriptionStatus | null } & Partial<SubscriptionStatus>>(START_TRIAL_PATH)
      return (data.subscription ?? data) as SubscriptionStatus
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_KEY })
      queryClient.invalidateQueries({ queryKey: TRIAL_DAYS_KEY })
    },
  })
}

export function useSubscribeCheckout() {
  return useMutation({
    mutationFn: async (input: { interval: 'monthly' | 'yearly' }) => {
      const { data } = await apiClient.post<{ checkout_url: string }>('/subscription/checkout/', input)
      return data.checkout_url
    },
    onSuccess: (url) => {
      if (url) window.location.href = url
    },
  })
}

export interface SeatUpdateResult {
  sponsorships_quota: number
  sponsorships_used: number
  sponsorships_available: number
}

export interface SeatPreview {
  currency: string
  interval: string
  seats_current: number
  seats_new: number
  seat_unit_amount: number
  seats_subtotal: number
  plan_name: string
  plan_amount: number
  /** Positive: charged today. Negative: credited against the next invoice. */
  amount_due_today: number
  recurring_subtotal: number
  recurring_discount: number
  recurring_total: number
  discount_label: string | null
}

/** Price a seat change without committing to it. Writes nothing. */
export function usePreviewSeats() {
  return useMutation({
    mutationFn: async (quantity: number) => {
      const { data } = await apiClient.post<SeatPreview>('/subscription/seats/preview/', { quantity })
      return data
    },
  })
}

/** Stripe amounts are in minor units. */
export function formatStripeAmount(amount: number, currency: string) {
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency.toUpperCase() })
    .format(Math.abs(amount) / 100)
}

export interface PlanOption {
  price_id: string
  lookup_key: string
  unit_amount: number
  currency: string
  /** Stripe's interval: 'month' | 'year'. */
  interval: string
  interval_key: 'monthly' | 'yearly'
  name: string
  is_current: boolean
}

export interface PlanChangePreview {
  currency: string
  interval: string
  current_interval: string
  plan_name: string
  plan_amount: number
  seats: number
  seat_unit_amount: number
  seats_subtotal: number
  amount_due_today: number
  recurring_subtotal: number
  recurring_discount: number
  recurring_total: number
  discount_label: string | null
}

export interface SubscriptionDiscount {
  label: string
  code: string | null
}

const PLANS_KEY = ['settings', 'plans']
const PROMO_KEY = ['settings', 'promo']

/** The discount currently applied to the subscription, if any. */
export function usePromoCode(enabled = true) {
  return useQuery({
    queryKey: PROMO_KEY,
    enabled,
    queryFn: async () => {
      const { data } = await apiClient.get<{ discount: SubscriptionDiscount | null }>('/subscription/promo/')
      return data.discount
    },
  })
}

/** Apply a promotion code to an existing subscription. Affects future invoices. */
export function useApplyPromoCode() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (code: string) => {
      const { data } = await apiClient.post<{ discount: SubscriptionDiscount | null }>('/subscription/promo/', { code })
      return data.discount
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROMO_KEY })
      queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_KEY })
    },
  })
}

export function useRemovePromoCode() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      await apiClient.delete('/subscription/promo/')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PROMO_KEY })
      queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_KEY })
    },
  })
}

/** The plans this account can hold. Only the billing interval is a choice. */
export function usePlans(enabled = true) {
  return useQuery({
    queryKey: PLANS_KEY,
    enabled,
    queryFn: async () => {
      const { data } = await apiClient.get<{ plans: PlanOption[] }>('/subscription/plans/')
      return data.plans
    },
  })
}

/** Price an interval change without committing to it. Writes nothing. */
export function usePreviewPlanChange() {
  return useMutation({
    mutationFn: async (interval: 'monthly' | 'yearly') => {
      const { data } = await apiClient.post<PlanChangePreview>('/subscription/plan/preview/', { interval })
      return data
    },
  })
}

export function useChangePlan() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (interval: 'monthly' | 'yearly') => {
      const { data } = await apiClient.post<{ interval: string; plan_name: string }>('/subscription/plan/', { interval })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_KEY })
      queryClient.invalidateQueries({ queryKey: PLANS_KEY })
    },
  })
}

/** Set the coach's seat count. Stripe prorates and charges the card on file. */
export function useUpdateSeats() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (quantity: number) => {
      const { data } = await apiClient.post<SeatUpdateResult>('/subscription/seats/', { quantity })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUBSCRIPTION_KEY })
    },
  })
}

/** First seat purchase rides a Stripe Checkout session (collects the card + promo codes). */
export function useSeatCheckout() {
  return useMutation({
    mutationFn: async (quantity: number) => {
      const { data } = await apiClient.post<{ checkout_url: string }>(
        '/subscription/seats/checkout/',
        { quantity },
      )
      return data.checkout_url
    },
  })
}
